import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";

import type { PromoGiftRule } from "@jti/commerce";
import { PromoGifts } from "@jti/commerce";
import { toast } from "@jti/core-components/components/Toaster";
import { getCurrentMagentoClient } from "@jti/magento";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import type { AvailableFreeGift } from "@jti/store/slices/cartSlice";
import { fetchCleanCartItems, removeItem } from "@jti/store/slices/cartSlice";
import { useGetPromoGiftInfoQuery } from "@jti/store/slices/magentoApiSlice";
import { useTranslations } from "@jti/ui";

export const CartPromoGiftsContainer: FC = () => {
  const { __ } = useTranslations();
  const dispatch = useDispatch();

  const cartId = useSelector((state) => state.cart.id) ?? "";
  const availableFreeGifts = useSelector((state) => state.cart.availableFreeGifts);
  const cartItems = useSelector((state) => state.cart.items);
  const orderLimitEnabled = useSelector((state) => state.cart.storeConfig?.is_order_limit_enabled);
  const [pendingSkus, setPendingSkus] = useState<Record<string, boolean>>({});

  const giftSkus = useMemo(() => {
    if (!availableFreeGifts) return [];
    return availableFreeGifts.flatMap((rule) => rule.allowed_gift_products.map((p) => p.sku));
  }, [availableFreeGifts]);

  const { data: promoGiftInfo } = useGetPromoGiftInfoQuery({ skus: giftSkus }, { skip: giftSkus.length === 0 });

  const giftInfoMap = useMemo(() => {
    if (!promoGiftInfo) return {};
    return Object.fromEntries(promoGiftInfo.map((info) => [info.sku, info]));
  }, [promoGiftInfo]);

  const getPromoGiftInCart = useCallback(
    (sku: string) => cartItems.find((item) => item.sku === sku && item.isGift && item.isGiftRemovable),
    [cartItems]
  );

  const getSelectedCountForRule = useCallback(
    (rule: AvailableFreeGift): number =>
      rule.allowed_gift_products.reduce((sum, product) => {
        const cartItem = getPromoGiftInCart(product.sku);
        return sum + (cartItem?.quantity || 0);
      }, 0),
    [getPromoGiftInCart]
  );

  const handleQuantityChange = async (salesRuleId: number, sku: string, upcomingValue: number) => {
    const rule = availableFreeGifts.find((r) => r.sales_rule_id === salesRuleId);
    if (!rule) return;

    const product = rule.allowed_gift_products.find((p) => p.sku === sku);
    if (!product) return;

    const cartItem = getPromoGiftInCart(sku);
    const currentValue = cartItem?.quantity || 0;

    if (upcomingValue === currentValue) return;

    const key = `${salesRuleId}_${sku}`;
    setPendingSkus((prev) => ({ ...prev, [key]: true }));

    try {
      const client = getCurrentMagentoClient();

      if (cartItem) {
        await dispatch(removeItem({ cartId, item: cartItem })).unwrap();
      }

      if (upcomingValue > 0) {
        const info = giftInfoMap[sku];
        const selectedOptions = info?.selectedOption ? [info.selectedOption] : undefined;
        await client.addPromoGiftToCart(cartId, [{ sku, quantity: upcomingValue, salesRuleId, selectedOptions }]);
      }

      dispatch(fetchCleanCartItems(cartId));
    } catch (err) {
      toast.error(__("cart.checkout.free_gifts.error"));
      console.error("Error updating promo gift quantity:", err);
    } finally {
      setPendingSkus((prev) => ({ ...prev, [key]: false }));
    }
  };

  const rules: PromoGiftRule[] = useMemo(() => {
    if (!availableFreeGifts || availableFreeGifts.length === 0) return [];

    return availableFreeGifts.map((rule) => ({
      salesRuleId: rule.sales_rule_id,
      giftQtyLimit: rule.gift_qty_limit,
      selectedCount: getSelectedCountForRule(rule),
      products: rule.allowed_gift_products.map((product) => ({
        sku: product.sku,
        name: product.name,
        imageUrl: giftInfoMap[product.sku]?.imageUrl,
        quantity: getPromoGiftInCart(product.sku)?.quantity || 0,
        pending: !!pendingSkus[`${rule.sales_rule_id}_${product.sku}`],
      })),
    }));
  }, [availableFreeGifts, giftInfoMap, pendingSkus, getPromoGiftInCart, getSelectedCountForRule]);

  if (rules.length === 0) return null;

  return <PromoGifts rules={rules} onQuantityChange={handleQuantityChange} orderLimitEnabled={orderLimitEnabled} />;
};
